/**
 * A title in a panel header, renameable in place.
 *
 * It is a BUTTON (and then an input) rather than a span because a panel header
 * doubles as the layout drag handle: `Panel` leaves a pointerdown on a
 * button/input alone, so a click here starts an edit instead of picking the
 * pane up. A bare span made the title undraggable-looking but ungrabbable text
 * — every attempt to click into it dragged the layout.
 *
 * Key it on the id of the thing being titled. The draft is local state, so
 * without a key an open edit carries across to whatever the header shows next.
 */

import { useEffect, useRef, useState } from 'react'

export function HeaderTitle({
  title,
  onRename,
  editTitle = 'Rename',
}: {
  title: string
  onRename: (title: string) => void
  /** Tooltip on the button, so each header can name what it renames. */
  editTitle?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(title)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  const commit = () => {
    const next = draft.trim()
    if (next && next !== title) onRename(next)
    setEditing(false)
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') {
            setDraft(title)
            setEditing(false)
          }
        }}
        // biome-ignore lint/a11y/noAutofocus: focus follows an explicit rename action
        autoFocus
        className="min-w-0 flex-1 rounded border border-border bg-background px-1.5 py-0.5 font-medium text-sm outline-none focus:border-blue-500"
      />
    )
  }

  return (
    <button
      type="button"
      title={editTitle}
      onClick={() => {
        setDraft(title)
        setEditing(true)
      }}
      className="min-w-[min(7rem,100%)] shrink truncate rounded px-1 py-0.5 text-left font-medium text-sm hover:bg-muted"
    >
      {title}
    </button>
  )
}
